import { Column } from '@app/components/data-grid/data-grid';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFlags } from '@app/hooks/use-flags';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo } from 'react';

import { DataGrid } from '../../components';
import { ErrorScreen } from '../../components/error-states/error-screen';
import { NoFilterResults } from '../../components/error-states/no-filter-results';
import { NoResults } from '../../components/error-states/no-results';
import {
  FLAG_ENABLE_ANALYTICS_LEAD_FILTER,
  FLAG_ENABLE_CH_DASHBOARD_ANALYTICS,
} from '../../constants';
import { usePaginatedFetch } from '../../hooks/use-paginated-fetch';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { fetchRegistrants } from '../../services/endpoints';
import { InterHeaderStyles } from '../../styles';
import { getNextPageCount } from '../../utils';
import { EngagementScoreTemplate } from './engagement-score-template';
import { EventsAttendedTemplate } from './events-attended-template';
import { LoaderSkeleton } from './loader-skeleton';
import { RegistrantType } from './types';

const TRANSLATION_KEYS = {
  firstName: 'analytics.first-name',
  lastName: 'analytics.last-name',
  contactType: 'analytics.contact-type',
  email: 'analytics.email',
  engagementScore: 'analytics.engagement-score',
  activity: 'analytics.activity',
  eventsRegistered: 'analytics.events-registered',
} as const;

const REGISTRANT_COLUMN_CONFIG = {
  firstName: {
    name: 'firstName',
    customCSS: InterHeaderStyles,
  },
  lastName: {
    name: 'lastName',
    customCSS: InterHeaderStyles,
  },
  contactType: {
    name: 'contactType',
    customCSS: InterHeaderStyles,
  },
  email: {
    name: 'email',
  },
  engagementScore: {
    name: 'engagementScore',
    template: EngagementScoreTemplate,
  },
  eventsCount: {
    name: 'eventsCount',
    template: EventsAttendedTemplate,
    width: '300px',
  },
} as const;

export const Registrants = () => {
  const { state, dispatch } = useAnalyticsContext();
  const { t } = useTranslation();
  const flags = useFlags();
  const orgId: string = useReadDashboardSharedState('org_id') || '';

  const enableLeadFilter = flags?.[FLAG_ENABLE_ANALYTICS_LEAD_FILTER];
  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];

  const columns = useMemo(
    () =>
      [
        {
          ...REGISTRANT_COLUMN_CONFIG.firstName,
          text: t(TRANSLATION_KEYS.firstName),
        },
        {
          ...REGISTRANT_COLUMN_CONFIG.lastName,
          text: t(TRANSLATION_KEYS.lastName),
        },
        enableCHDashboardAnalytics && {
          ...REGISTRANT_COLUMN_CONFIG.contactType,
          text: t(TRANSLATION_KEYS.contactType),
          formatter: (value: string) =>
            t(`analytics.${value === 'contact' ? 'hub-lead' : 'registrant'}`),
        },
        {
          ...REGISTRANT_COLUMN_CONFIG.email,
          text: t(TRANSLATION_KEYS.email),
        },
        {
          ...REGISTRANT_COLUMN_CONFIG.engagementScore,
          text: t(TRANSLATION_KEYS.engagementScore),
        },
        {
          ...REGISTRANT_COLUMN_CONFIG.eventsCount,
          text: t(
            enableCHDashboardAnalytics
              ? TRANSLATION_KEYS.activity
              : TRANSLATION_KEYS.eventsRegistered,
          ),
        },
      ].filter(Boolean) as Column<RegistrantType>[],
    [enableCHDashboardAnalytics, t],
  );

  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    eventTypes: state.eventTypes,
    ...(enableLeadFilter && {
      contactType: state.contactType,
    }),
    name: state.registrantSortName,
    direction: state.registrantSortDirection,
    searchText: state.searchText,
    selectedEvents: state.selectedEvents,
    ...(enableCHDashboardAnalytics && {
      includeContacts: true,
      includeUpcoming: true,
      eventNames: [...(state.selectedVideos || [])],
    }),
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoad,
  } = usePaginatedFetch(
    { ...config },
    `registrants-${orgId}`,
    useCallback(fetchRegistrants, []),
  );
  const { track } = useAnalytics();

  const handleSort = (columnKey: string, direction: 'asc' | 'desc' | '') => {
    dispatch({
      type: 'update registrants sort key and direction',
      payload: { name: columnKey, direction: direction },
    });
    track('ola_registrants_tab_sorting_click', {
      columnKey: columnKey,
      direction: direction,
    });
  };

  const fetchMore = () => {
    if (!isLoading && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const result = {
    registrantsData: data?.pages.flatMap(page => page.registrants) || [],
    pagination: data?.pages[data.pages.length - 1]?.pagination || {},
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DataGrid<RegistrantType>
      columns={columns}
      data={result.registrantsData || []}
      showLoader={(isLoading || isFetchingNextPage) && !isError}
      loader={LoaderSkeleton}
      loaderCount={getNextPageCount(result.pagination)}
      onSort={handleSort}
      fetchMore={fetchMore}
      sortConfig={{
        name: state.registrantSortName as keyof RegistrantType,
        direction: state.registrantSortDirection as '' | 'asc' | 'desc',
      }}
      noResults={() => (isInitialLoad ? NoResults : NoFilterResults)}
      error={props => <ErrorScreen {...props} showButton={true} />}
      isError={isError}
    />
  );
};
